import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import api from '../utils/axios';
import { handleApiError } from '../utils/errorHandler';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

interface Company {
  id: string;
  name: string;
  type: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  status: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  contacts?: Contact[];
  claims?: Claim[];
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
}

interface Claim {
  id: string;
  claimNumber: string;
  status: string;
}

interface Metrics {
  totalCompanies: number;
  activeCompanies: number;
  insuranceCompanies: number;
  otherCompanies: number;
}

const CompanyManagement: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalCompanies: 0,
    activeCompanies: 0,
    insuranceCompanies: 0,
    otherCompanies: 0
  });

  const debouncedSearchText = useDebounce(searchText, 400);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Company[]; pagination: any }>('/companies', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: debouncedSearchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD')
        }
      });
      if (response.data.success) {
        setCompanies(response.data.data);
        // Update pagination from response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.data.pagination.current,
            pageSize: response.data.pagination.pageSize,
            total: response.data.pagination.total
          }));
        }
        calculateMetrics(response.data.data);
      } else {
        message.error('Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, debouncedSearchText, dateRange]);

  const calculateMetrics = (data: Company[]) => {
    setMetrics({
      totalCompanies: data.length,
      activeCompanies: data.filter(company => company.status === 'ACTIVE').length,
      insuranceCompanies: data.filter(company => company.type === 'INSURANCE_COMPANY').length,
      otherCompanies: data.filter(company => company.type !== 'INSURANCE_COMPANY').length
    });
  };

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Reset pagination when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [debouncedSearchText, dateRange]);

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setIsModalVisible(true);
    form.setFieldsValue(company);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/companies/${id}`);
      message.success('Company deleted successfully');
      fetchCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      message.error('Failed to delete company');
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, values);
        message.success('Company updated successfully');
      } else {
        await api.post('/companies', values);
        message.success('Company created successfully');
      }
      setIsModalVisible(false);
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      message.error('Failed to save company');
    }
  };

  const columns: ColumnsType<Company> = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      width: 200,
      fixed: 'left',
      render: (_, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 'inherit' }}
          onClick={() => navigate(`/companies/${record.id}`)}
        >
          {record.name}
        </Button>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const color = type === 'INSURANCE_COMPANY' ? 'blue' : 
                     type === 'BUSINESS' ? 'green' : 
                     type === 'VENDOR' ? 'orange' : 'default';
        return (
          <Tag color={color}>
            {type === 'INSURANCE_COMPANY' ? 'Insurance' : type}
          </Tag>
        );
      },
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      width: 150,
      render: (industry: string) => industry || 'N/A',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string) => email || 'N/A',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone: string) => phone || 'N/A',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'success' : 'error'}>
          {status}
        </Tag>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Contacts',
      key: 'contacts',
      width: 100,
      render: (_, record) => (
        <Tag color="blue">
          {record.contacts?.length || 0} contacts
        </Tag>
      ),
    },
    {
      title: 'Claims',
      key: 'claims',
      width: 100,
      render: (_, record) => (
        <Tag color="green">
          {record.claims?.length || 0} claims
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/companies/${record.id}`)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<Company>['onChange'] = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(prev => ({
        ...prev,
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 10
      }));
    }

    if (sorter) {
      const { field, order } = sorter as { field: keyof Company; order: 'ascend' | 'descend' | null };
      const sortedData = [...companies].sort((a, b) => {
        const aValue = a[field] ?? '';
        const bValue = b[field] ?? '';
        
        if (order === 'ascend') {
          return String(aValue).localeCompare(String(bValue));
        }
        if (order === 'descend') {
          return String(bValue).localeCompare(String(aValue));
        }
        return 0;
      });
      setCompanies(sortedData);
    }
  };

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Companies"
              value={metrics.totalCompanies}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Companies"
              value={metrics.activeCompanies}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Insurance Companies"
              value={metrics.insuranceCompanies}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Other Companies"
              value={metrics.otherCompanies}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Companies</Title>
          <Space>
            <Input
              placeholder="Search companies..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Company
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
          size="middle"
          bordered
          style={{ overflowX: 'auto' }}
        />
      </Card>

      <Modal
        title={editingCompany ? 'Edit Company' : 'New Company'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Company Type"
                rules={[{ required: true, message: 'Please select company type' }]}
              >
                <Select>
                  <Option value="BUSINESS">Business</Option>
                  <Option value="VENDOR">Vendor</Option>
                  <Option value="SUPPLIER">Supplier</Option>
                  <Option value="INSURANCE_COMPANY">Insurance Company</Option>
                  <Option value="OTHER">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="industry"
                label="Industry"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="website"
                label="Website"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="state"
                label="State"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="zipCode"
                label="ZIP Code"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="country"
            label="Country"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
              <Option value="PENDING">Pending</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyManagement; 